import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { Injectable } from "@angular/core";
import "rxjs/add/observable/of";
import "rxjs/add/operator/do";
import "rxjs/add/operator/delay";
import "rxjs/add/operator/map";
import "rxjs/add/operator/catch";
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from "../../../environments/environment";
import { UtilitiesService } from "./utilities.service";
import { Router } from "@angular/router";
import { Auth } from 'aws-amplify';
import { EXCEPTION_TYPES } from '../constants';

@Injectable()
export class AuthService {

    isLoginSubject = new BehaviorSubject<boolean>(this.tokenNotExpired());

    loginWithTokenSubject = new BehaviorSubject('');
    loginWithTokenObservable = this.loginWithTokenSubject.asObservable();
    congitoUser = new BehaviorSubject({});
    userOrganizationId = 0;

    constructor(private http: HttpClient,
        private utilitiesService: UtilitiesService,
        private router: Router) {
        const organizationId = JSON.parse(this.utilitiesService.getLocal("userData"))?.organizationId;
        if (organizationId > 0) {
            this.userOrganizationId = organizationId;
        }
    } // constructor()

    async logout(): Promise<void> {
        try {
            await Auth.signOut();
            // blast local storage - have a clean session for the next user
            // save market, promoCodes, session and persistent cookies though
            let market = this.utilitiesService.getLocal("market");
            let promoCodes = this.utilitiesService.getLocal("promoCodes");
            let sessionKey = this.utilitiesService.getLocal("sessionKey");
            let persistentKey = this.utilitiesService.getLocal("persistentKey");
            let referrerId = this.utilitiesService.getLocal("referrerId");
            let referrerType = this.utilitiesService.getLocal("referrerType");
            this.utilitiesService.clearStorage();
            this.utilitiesService.setLocal("market", market);
            this.utilitiesService.setLocal("promoCodes", promoCodes);
            this.utilitiesService.setLocal("sessionKey", sessionKey);
            this.utilitiesService.setLocal("persistentKey", persistentKey);
            this.utilitiesService.setLocal("referrerId", referrerId);
            this.utilitiesService.setLocal("referrerType", referrerType);
            this.isLoginSubject.next(false);
            this.userOrganizationId = 0;
        } catch (error) {
            console.log('error signing out: ', error);
        }
    } // logout()

    /**
     *
     * @returns {Observable<T>}
     */
    loggedInObservable(): Observable<boolean> {
        return this.isLoginSubject.asObservable();
    }

    loggedIn() {
        return this.tokenNotExpired(); // todo delete this method
    }
    // todo should just check if token exists in locale storage, to check on valid token
    // site interceptor should handle token validation by using amplify

    updateLogin(token: any) {
        this.utilitiesService.setLocal("loginWithToken", true);
        this.utilitiesService.setLocal("token", token);
        this.isLoginSubject.next(true);
    }

    /**
     * pretty straight forward, this logs in a user with SSO
     * @param credentials
     */
    async loginWithSso(credentials: any) {
        // clear userData and token
        this.utilitiesService.removeLocal('token');
        this.utilitiesService.removeLocal('userData');
        this.utilitiesService.removeLocal('aws');
        // this.logout();

        try {
            const user = await Auth.signIn(credentials.username.toLowerCase(), credentials.password);
            if (user.challengeName === EXCEPTION_TYPES.NEW_PASSWORD_REQUIRED) {
                this.congitoUser.next(user);
                return user;
            } else {
                const idToken = user.signInUserSession.idToken.jwtToken;
                const accessToken = user.signInUserSession.accessToken.jwtToken;
                try {
                    let tokenData = this.decodeToken(idToken);
                    if (tokenData['custom:is_user'] === 'true') {
                        this.utilitiesService.setLocal("token", accessToken);
                        this.utilitiesService.setLocal("idToken", idToken);

                        if (!UtilitiesService.isEmpty(tokenData.given_name)) {
                            this.utilitiesService.setLocal("loginName", tokenData.given_name);
                        }

                        this.isLoginSubject.next(true);
                    } else {
                        // custom response code
                        user.responseCode = -10;
                    }
                } catch (e) {
                    // set to unexpected error
                    user.responseCode = 200;
                }
            }
            return user;
        } catch (err) {
            return err;
        }
    } // login()

    /**
     * pretty straight forward, this logs in a user with Wrench DB
     * @param credentials
     */
    loginWithWrench(credentials: any) {

        // clear userData and token
        this.utilitiesService.removeLocal('token');
        this.utilitiesService.removeLocal('userData');
        this.utilitiesService.removeLocal('aws');

        return this.http.post<any>(this.getBackendUrl("/user/login"), credentials, this.getBasicAuthHeaders())
            .map((data: any) => {
                if (data.responseCode == 100 && !this.utilitiesService.isEmpty(data.token)) {
                    try {
                        let tokenData = this.decodeToken(data.token);
                        if (tokenData.admin === true) {
                            // custom admin response code
                            data.responseCode = -10;
                        } else {
                            this.utilitiesService.setLocal("token", data.token);
                            if (!UtilitiesService.isEmpty(data.loginName)) {
                                this.utilitiesService.setLocal("loginName", data.loginName);
                            }
                            this.isLoginSubject.next(true);
                        }
                    } catch (e) {
                        // set to unexpected error
                        data.responseCode = 200;
                    }
                }
                return data;
            })
            .catch(error => {
                return this.handleError(error);
            }
            );
    } // login()

    loginWithToken(token: any) {
        // clear userData and token
        this.utilitiesService.removeLocal('token');
        this.utilitiesService.removeLocal('userData');
        this.utilitiesService.removeLocal('aws');
        this.utilitiesService.clearStorage();
        this.utilitiesService.setLocal("token", token);
        this.utilitiesService.setLocal("idToken", token);
        this.isLoginSubject.next(true);
        this.utilitiesService.setLocal("loginWithToken", true);
        this.loginWithTokenSubject.next(token);
        return this.loginWithTokenObservable;
    } // loginWithToken()

    /**
     * Decodes the token - used for almost all the below functions
     */
    private decodeToken(token?: String) {
        if (this.utilitiesService.isEmpty(token)) {
            const loginWithToken = this.utilitiesService.getLocal('loginWithToken') ? JSON.parse(this.utilitiesService.getLocal('loginWithToken')) : false;
            if (loginWithToken) {
                token = this.utilitiesService.getLocal('token');
            } else {
                token = this.utilitiesService.getLocal('idToken');
            }
        }

        try {
            if (!this.utilitiesService.isEmpty(token)) {
                // get token data
                let tokenArray = token.split(".");
                let tokenDataString = atob(tokenArray[1]);
                let tokenData = JSON.parse(tokenDataString);

                return tokenData;
            }

        } catch (e) {
            // do nothing
        }

        return {};
    }

    /**
     * Checks for presence of token and that token hasn't expired.
     * For use with the @CanActivate router decorator and NgIf
     */

    // todo should just check if token exists in locale storage, to check on valid token
    // site interceptor should handle token validation by using amplify
    public tokenNotExpired(token?: String) {
        try {
            const tokenData = this.decodeToken(token);
            if (!this.utilitiesService.isEmpty(tokenData)) {
                // check expiration of token
                let now = Math.floor(Date.now() / 1000);

                if (tokenData.exp <= now) {
                    this.logout()
                }

                return tokenData.exp > now;
            }

        } catch (e) {
            // do nothing
        }

        return false;
    }

    public getUserIdFromToken(token?: String) {
        try {
            const tokenData = this.decodeToken(token);
            if (!this.utilitiesService.isEmpty(tokenData)) {
                return tokenData['custom:wrench_user_id'] ? Number(tokenData['custom:wrench_user_id']) : tokenData.userid;
            }
        } catch (e) {
            // do nothing
        }
        return null;

    } // getUserIdFromToken()

    public getProviderFromToken(token?: String) {
        try {
            const tokenData = this.decodeToken(token);
            if (!this.utilitiesService.isEmpty(tokenData)) {
                return tokenData.provider;
            }
        } catch (e) {
            // do nothing
        }

        return false;

    } // getProviderFromToken()

    public getEmailFromToken(token?: String) {
        try {
            const tokenData = this.decodeToken(token);
            if (!this.utilitiesService.isEmpty(tokenData)) {
                return tokenData.email ? tokenData.email : tokenData.sub;
            }
        } catch (e) {
            // do nothing
        }

        return "";

    } // getEmailFromToken()

    // todo there's something to fix here but i forget. eddie might remember
    public isTemporaryToken(token?: String) {
        try {
            const tokenData = this.decodeToken(token);
            if (!this.utilitiesService.isEmpty(tokenData)) {
                return "aud" in tokenData;
            }
        } catch (e) {
            // do nothing
        }

        return false;

    } // isTemporaryToken()

    /**
     * this saves the token if the current token is empty
     * of replaces it if the current token is a temporary token or the users in the tokens don't match
     * @param token
     * @return boolean - true if we replaced/saved the current token
     */
    public async replaceCurrentToken(token: any): Promise<boolean> {
        let currentToken = this.utilitiesService.getLocal("token");
        if (UtilitiesService.isEmpty(currentToken)) {
            await Auth.signOut();
            this.utilitiesService.removeLocal('userData');
            this.updateLogin(token);
            return true;
        } else if (!this.tokenNotExpired() || this.isTemporaryToken() || this.getUserIdFromToken() != this.getUserIdFromToken(token)) {
            await Auth.signOut();
            this.utilitiesService.removeLocal('userData');
            this.updateLogin(token);
            return true;
        }
        return false;
    } // replaceCurrentToken()

    public getAuthHeaders(): {headers: HttpHeaders} {
        let headers: HttpHeaders = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + localStorage.getItem('token'),
        });
        const loginWithToken = this.utilitiesService.getLocal('loginWithToken') ? JSON.parse(this.utilitiesService.getLocal('loginWithToken')) : false;
        if (!loginWithToken) {
            headers = headers.append('cognito_sso', 'true');
        }
        return { headers };
    }

    public getBasicAuthHeaders() {
        return {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Basic ' + btoa(environment.http_params.authUsername + ':' + environment.http_params.authPassword)
            })
        };
    }

    public authenticateToken(params: any) {
        return this.http.post(this.getBackendUrl("/user/login"), JSON.stringify(params), this.getBasicAuthHeaders())
            .catch(error => {
                return this.handleError(error);
            });

    } // authenticateToken()

    private handleError(error: any) {

        // check for http 403 or 401 or 420 error
        if (error.status == 403 || error.status == 401) {
            this.router.navigate(['/login']);
            return null;
        } else if (error.status == 420) {
            this.router.navigate(['/activateaccount']);
            return null;
        }

        // In a real world app, we might use a remote logging infrastructure
        // We'd also dig deeper into the error to get a better message
        let errMsg = (error.message) ? error.message :
            error.status ? `${error.status} - ${error.statusText}` : 'Server error';
        return throwError(errMsg);

    } // handleError()

    async changePassword(params: any) {
        const user = await Auth.currentAuthenticatedUser();
        return await Auth.changePassword(user, params.oldPassword, params.password);
    } // changePassword()

    async completeNewPassword(params: any) {
        const currentUser = this.congitoUser.getValue();
        try {
            const user = await Auth.completeNewPassword(currentUser, params.newPassword);
            if (user && this.utilitiesService.isNotEmpty(user.signInUserSession)) {
                const idToken = user.signInUserSession.idToken.jwtToken;
                const accessToken = user.signInUserSession.accessToken.jwtToken;
                try {
                    let tokenData = this.decodeToken(idToken);
                    if (tokenData['custom:is_user'] === 'true') {
                        this.utilitiesService.setLocal("token", accessToken);
                        this.utilitiesService.setLocal("idToken", idToken);

                        if (!UtilitiesService.isEmpty(tokenData.given_name)) {
                            this.utilitiesService.setLocal("loginName", tokenData.given_name);
                        }
                        this.isLoginSubject.next(true);
                    } else {
                        // custom response code
                        user.responseCode = -10;
                    }
                } catch (e) {
                    // set to unexpected error
                    user.responseCode = 200;
                }
            }
            return user;
        } catch (err) {
            return err;
        }
    } // changePassword()

    async getAccessToken() {
        return Auth.currentSession().then(session => {
            const accessToken = session.getAccessToken().getJwtToken();
            this.utilitiesService.setLocal("token", accessToken);
            return accessToken;
        }).catch(err => {
            return '';
        });
    } // getAccessToken

    public getBackendUrl(url: string) {
        return environment.http_params.baseUrl + url;
    } // getBackendUrl
}
